import { useEffect, useState } from "react";
import BrandLogo, { LOGO_URL } from "./components/common/BrandLogo";

const BLUE    = "#0077B5";
const BLUE_DK = "#005885";
const BLUE_LT = "#EBF5FB";
const BG      = "#F3F2EF";
const CARD    = "#FFFFFF";
const BORDER  = "#E1E9EE";
const TEXT    = "#1A1A1A";
const MUTED   = "#666666";
const GREEN   = "#057642";

const CSS = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { background: ${BG}; font-family: -apple-system,'Segoe UI',Helvetica,Arial,sans-serif; color: ${TEXT}; -webkit-font-smoothing: antialiased; }
  a { text-decoration: none; color: inherit; }

  .btn-primary {
    background: ${BLUE}; color: #fff; border: none; border-radius: 24px;
    padding: 14px 32px; font-size: 15px; font-weight: 600; cursor: pointer;
    font-family: -apple-system,'Segoe UI',Helvetica,sans-serif;
    transition: background 0.15s; display: inline-block; min-height: 44px;
  }
  .btn-primary:hover { background: ${BLUE_DK}; }
  .btn-ghost {
    background: transparent; color: ${BLUE}; border: 1.5px solid ${BLUE};
    border-radius: 24px; padding: 13px 28px; font-size: 15px; font-weight: 600;
    cursor: pointer; font-family: -apple-system,'Segoe UI',Helvetica,sans-serif;
    transition: all 0.15s; display: inline-block; min-height: 44px;
  }
  .btn-ghost:hover { background: ${BLUE_LT}; }

  .nav-link { font-size: 15px; color: ${MUTED}; transition: color 0.15s; cursor: pointer; }
  .nav-link:hover { color: ${TEXT}; }

  .fade-up { opacity: 0; transform: translateY(24px); transition: opacity 0.6s ease, transform 0.6s ease; }
  .fade-up.visible { opacity: 1; transform: none; }

  @media (max-width: 900px) {
    .hero-grid { grid-template-columns: 1fr !important; }
    .hero-right { display: none !important; }
    .steps-grid { grid-template-columns: 1fr 1fr !important; }
    .pricing-grid { grid-template-columns: 1fr !important; border-radius: 12px !important; }
    .testi-grid { grid-template-columns: 1fr !important; }
    .nav-links-desktop { display: none !important; }
    .nav-cta-desktop { display: none !important; }
    .nav-hamburger { display: block !important; }
    .hero-section { padding: 56px 24px !important; }
    .page-section { padding: 64px 24px !important; }
    nav { padding: 0 20px !important; }
    footer { padding: 28px 24px !important; flex-direction: column !important; align-items: flex-start !important; }
    h1.hero-h1 { font-size: 44px !important; }
    h2.section-h2 { font-size: 32px !important; }
  }
  @media (max-width: 480px) {
    .steps-grid { grid-template-columns: 1fr !important; }
    .hero-cta { flex-direction: column !important; align-items: stretch !important; }
    .hero-cta .btn-primary, .hero-cta .btn-ghost { text-align: center; }
    h1.hero-h1 { font-size: 36px !important; }
  }
`;

// ── Scroll-triggered fade-up ──────────────────────────────────────────────────
function useFadeUp() {
  useEffect(() => {
    const els = document.querySelectorAll(".fade-up");
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.12 }
    );
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

// ── Mock Dashboard ────────────────────────────────────────────────────────────
function MockDashboard() {
  const posts = [
    { day: "Mon", cat: "Business", title: "Why SaaS Founders Are Ditching VC in 2026", status: "approved" },
    { day: "Wed", cat: "Real Estate", title: "Canadian Real Estate: Where Smart Money Moves", status: "approved" },
    { day: "Fri", cat: "Health", title: "Mental Health in the Workplace: The Numbers", status: "pending" },
  ];
  return (
    <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.12)", overflow: "hidden", maxWidth: 440, width: "100%" }}>
      <div style={{ background: CARD, borderBottom: `1px solid ${BORDER}`, padding: "12px 18px", display: "flex", alignItems: "center", gap: 10 }}>
        <img src={LOGO_URL} alt="" style={{ width: 26, height: 26, borderRadius: 6, objectFit: "cover" }} />
        <span style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 16, fontWeight: 700, color: BLUE }}>BrandHives</span>
        <span style={{ fontSize: 11, color: MUTED, marginLeft: 2 }}>by BHIVE™</span>
        <span style={{ marginLeft: "auto", fontSize: 12, color: MUTED }}>Week of Mar 10</span>
      </div>
      <div style={{ padding: "14px 18px", borderBottom: `1px solid ${BORDER}`, background: BLUE_LT }}>
        <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 18, fontWeight: 400, marginBottom: 8 }}>
          Your posts are ready, <em style={{ color: BLUE }}>Rod.</em>
        </div>
        <div style={{ display: "flex", gap: 24, alignItems: "flex-end" }}>
          {[["5","posts"],["2","approved"],["3","pending"]].map(([n, l]) => (
            <div key={l}>
              <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 20, fontWeight: 700, color: BLUE }}>{n}</div>
              <div style={{ fontSize: 11, color: MUTED }}>{l}</div>
            </div>
          ))}
          <button style={{ marginLeft: "auto", background: TEXT, color: "#fff", border: "none", borderRadius: 6, padding: "7px 16px", fontSize: 11, fontWeight: 600, letterSpacing: "0.5px", cursor: "pointer" }}>
            Approve All
          </button>
        </div>
      </div>
      <div style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 8 }}>
        {posts.map((p, i) => (
          <div key={i} style={{ border: `1px solid ${p.status === "approved" ? "#B7E4C7" : BORDER}`, borderRadius: 8, padding: "11px 14px", display: "flex", alignItems: "center", gap: 12, background: p.status === "approved" ? "#F0FAF4" : CARD }}>
            <div style={{ minWidth: 32, textAlign: "center" }}>
              <div style={{ fontSize: 11, color: MUTED, fontWeight: 600 }}>{p.day}</div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: BLUE, fontWeight: 500, marginBottom: 2 }}>{p.cat}</div>
              <div style={{ fontSize: 13, color: TEXT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: p.status === "approved" ? GREEN : MUTED, flexShrink: 0 }}>
              {p.status === "approved" ? "✓ Live" : "Review"}
            </div>
          </div>
        ))}
      </div>
      <div style={{ margin: "0 18px 16px", background: TEXT, borderRadius: 8, padding: "10px 16px", display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>2 approved · 0 rejected</div>
        <button style={{ marginLeft: "auto", background: BLUE, color: "#fff", border: "none", borderRadius: 6, padding: "6px 16px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
          Confirm →
        </button>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function NectarLanding({ onStart, onSignIn, scrollTo }) {
  useFadeUp();

  useEffect(() => {
    if (scrollTo) {
      const el = document.getElementById(scrollTo);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, [scrollTo]);

  const STEPS = [
    { icon: "🔗", n: "01", title: "Connect LinkedIn", desc: "Authorise BrandHives with one click. We use official LinkedIn APIs — your credentials are never stored." },
    { icon: "🎯", n: "02", title: "Pick your interests", desc: "Choose the topics you care about. BrandHives curates fresh, relevant news from thousands of sources daily." },
    { icon: "✍️", n: "03", title: "We write in your voice", desc: "Our AI studies your tone and rewrites every post to sound authentically like you — not like a robot." },
    { icon: "✅", n: "04", title: "Approve once a week", desc: "Every Monday you get an email with the week's posts. One click to approve. Done in under 5 minutes." },
  ];

  const [annual, setAnnual] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const PLANS = [
    { name: "Presence",  slug: "presence",  monthly: 99,  yearly: 79.20,  tagline: "Stay consistently visible.", features: ["AI-assisted LinkedIn writing","Scheduling & content queue","Voice settings","Basic analytics"], featured: false },
    { name: "Authority", slug: "authority", monthly: 199, yearly: 159.20,  tagline: "Your expertise, systemized.", features: ["Everything in Presence","Industry & news-assisted posts","Smart promotional cadence","Advanced voice adaptation","Engagement optimization","Content memory"], featured: true, badge: "Most Popular" },
    { name: "Pro",       slug: "authority_pro", monthly: 299, yearly: 239.20, tagline: "Post like the specialist you are.", features: ["Everything in Authority","Expert commentary in your field","Matched to your jurisdiction","Curated professional content"], featured: false },
  ];

  const TESTIMONIALS = [
    { quote: "I hadn't posted on LinkedIn in two years. Within three weeks of using BrandHives I had three inbound leads from my content.", name: "Sarah M.", role: "Head of Growth · Series B startup", init: "S" },
    { quote: "I was skeptical the posts would sound like me. Read the first batch and genuinely couldn't tell which ones I wrote vs BrandHives.", name: "James T.", role: "Founder · B2B SaaS", init: "J" },
    { quote: "The Monday approval email takes me literally four minutes. The rest of the week LinkedIn just works. Worth every penny.", name: "Anika R.", role: "VP Sales · Enterprise SaaS", init: "A" },
  ];

  return (
    <div style={{ background: BG, minHeight: "100vh", color: TEXT }}>
      <style>{CSS}</style>

      {/* ── NAV ─────────────────────────────────────────────────────── */}
      <nav style={{ position: "sticky", top: 0, background: "rgba(255,255,255,0.97)", backdropFilter: "blur(8px)", borderBottom: `1px solid ${BORDER}`, padding: "0 48px", height: 68, display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 100, boxShadow: "0 1px 3px rgba(0,0,0,0.07)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src={LOGO_URL} alt="BrandHives" style={{ width: 34, height: 34, borderRadius: 8, objectFit: "cover" }} />
          <div>
            <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 21, fontWeight: 700, color: BLUE, lineHeight: 1.1 }}>BrandHives</div>
            <div style={{ fontSize: 11, color: MUTED, lineHeight: 1 }}>by BHIVE™</div>
          </div>
        </div>

        <div className="nav-links-desktop" style={{ display: "flex", gap: 36, alignItems: "center" }}>
          <a href="#how" className="nav-link">How it works</a>
          <a href="#pricing" className="nav-link">Pricing</a>
          <a href="#reviews" className="nav-link">Reviews</a>
        </div>

        <div className="nav-cta-desktop" style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button className="btn-ghost" onClick={onSignIn} style={{ padding: "9px 20px", fontSize: 14 }}>Sign in</button>
          <button className="btn-primary" onClick={onStart} style={{ padding: "9px 20px", fontSize: 14 }}>Start Free Trial</button>
        </div>

        <button className="nav-hamburger" onClick={() => setMobileMenuOpen(m => !m)} aria-label="Toggle menu" style={{ display: "none", background: "none", border: "none", cursor: "pointer", padding: 8, lineHeight: 1 }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 24, height: 24, color: "#1a1a1a" }}>
            <path d="M3 12h18M3 6h18M3 18h18" />
          </svg>
        </button>
      </nav>

      {mobileMenuOpen && (
        <div style={{ position: "fixed", top: 68, left: 0, right: 0, bottom: 0, zIndex: 98 }} onClick={() => setMobileMenuOpen(false)}>
          <div style={{ background: "#fff", borderBottom: `1px solid ${BORDER}`, boxShadow: "0 4px 12px rgba(0,0,0,0.08)", padding: "16px 24px", display: "flex", flexDirection: "column", gap: 16, zIndex: 99, position: "relative" }} onClick={e => e.stopPropagation()}>
            <a href="#how" className="nav-link" style={{ fontSize: 15, padding: "8px 0" }} onClick={() => setMobileMenuOpen(false)}>How it works</a>
            <a href="#pricing" className="nav-link" style={{ fontSize: 15, padding: "8px 0" }} onClick={() => setMobileMenuOpen(false)}>Pricing</a>
            <a href="#reviews" className="nav-link" style={{ fontSize: 15, padding: "8px 0" }} onClick={() => setMobileMenuOpen(false)}>Reviews</a>
            <div style={{ height: 1, background: BORDER }} />
            <button className="nav-link" onClick={() => { setMobileMenuOpen(false); onSignIn(); }} style={{ background: "none", border: "none", textAlign: "left", cursor: "pointer", fontSize: 15, padding: "8px 0", fontFamily: "inherit" }}>Sign In</button>
            <button className="nav-link" onClick={() => { setMobileMenuOpen(false); onStart(); }} style={{ background: "none", border: "none", textAlign: "left", cursor: "pointer", fontSize: 15, padding: "8px 0", fontFamily: "inherit", color: BLUE, fontWeight: 600 }}>Start Free</button>
          </div>
        </div>
      )}

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section className="hero-section" style={{ padding: "88px 48px", borderBottom: `1px solid ${BORDER}` }}>
        <div className="hero-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center", maxWidth: 1200, margin: "0 auto" }}>
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: BLUE_LT, border: `1px solid ${BORDER}`, borderRadius: 24, padding: "6px 16px", marginBottom: 28 }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: BLUE }} />
              <span style={{ fontSize: 13, color: BLUE, fontWeight: 500 }}>Your expertise, systemized</span>
            </div>
            <h1 className="hero-h1" style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 62, fontWeight: 700, lineHeight: 1.1, letterSpacing: "-1px", marginBottom: 24 }}>
              Your expertise,<br /><em style={{ color: BLUE }}>systemized.</em>
            </h1>
            <p style={{ fontSize: 18, color: MUTED, lineHeight: 1.75, maxWidth: 460, marginBottom: 36 }}>
              Stay consistently visible on LinkedIn. BrandHives reduces the pressure of content creation so you can focus on running your business.
            </p>
            <div className="hero-cta" style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 40 }}>
              <button className="btn-primary" onClick={onStart} style={{ padding: "16px 36px", fontSize: 16 }}>
                Start Free — 30 Days
              </button>
              <a href="#how" className="btn-ghost" style={{ padding: "15px 28px", fontSize: 16 }}>
                See how it works ↓
              </a>
            </div>
            <div style={{ display: "flex", gap: 32, paddingTop: 28, borderTop: `1px solid ${BORDER}` }}>
              {[["500+","Active users"],["4.9 ★","Avg. rating"],["5 min","Per week"]].map(([n, l]) => (
                <div key={l}>
                  <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 26, fontWeight: 700, color: TEXT }}>{n}</div>
                  <div style={{ fontSize: 13, color: MUTED }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="hero-right" style={{ display: "flex", justifyContent: "center", position: "relative" }}>
            <div style={{ position: "absolute", top: -14, right: -14, background: GREEN, color: "#fff", padding: "8px 16px", borderRadius: 6, fontSize: 12, fontWeight: 600, boxShadow: "0 4px 14px rgba(0,0,0,0.14)", zIndex: 1 }}>
              Your week is ready ✓
            </div>
            <MockDashboard />
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────── */}
      <section id="how" className="page-section" style={{ padding: "96px 48px", borderBottom: `1px solid ${BORDER}`, background: CARD }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase", color: BLUE, marginBottom: 12 }}>How it works</div>
          <h2 className="section-h2 fade-up" style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 42, fontWeight: 700, lineHeight: 1.15, marginBottom: 16 }}>
            Four steps to a<br /><em style={{ color: BLUE }}>consistent LinkedIn presence</em>
          </h2>
          <p className="fade-up" style={{ fontSize: 17, color: MUTED, lineHeight: 1.7, maxWidth: 480, marginBottom: 56 }}>
            Set it up once. Approve posts weekly. BrandHives handles everything in between.
          </p>
          <div className="steps-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1, background: BORDER, border: `1px solid ${BORDER}`, borderRadius: 12, overflow: "hidden" }}>
            {STEPS.map(s => (
              <div key={s.n} className="fade-up" style={{ background: CARD, padding: "36px 28px", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", right: 16, top: 12, fontFamily: "'Playfair Display',Georgia,serif", fontSize: 56, fontWeight: 700, color: BLUE_LT, lineHeight: 1, userSelect: "none" }}>{s.n}</div>
                <div style={{ fontSize: 28, marginBottom: 16 }}>{s.icon}</div>
                <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 19, fontWeight: 600, marginBottom: 10 }}>{s.title}</div>
                <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.65 }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ──────────────────────────────────────────────────── */}
      <section id="pricing" className="page-section" style={{ padding: "96px 48px", borderBottom: `1px solid ${BORDER}`, background: BG }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase", color: BLUE, marginBottom: 12 }}>Pricing</div>
          <h2 className="section-h2 fade-up" style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 42, fontWeight: 700, lineHeight: 1.15, marginBottom: 16 }}>
            Maintain your authority.<br /><em style={{ color: BLUE }}>We handle the rest.</em>
          </h2>
          <p className="fade-up" style={{ fontSize: 17, color: MUTED, lineHeight: 1.7, maxWidth: 520, marginBottom: 32 }}>
            Reduce the pressure of content creation. Stay visible while you run your business.
          </p>

          {/* ── Billing toggle ── */}
          <div className="fade-up" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginBottom: 40 }}>
            <span style={{ fontSize: 15, fontWeight: annual ? 400 : 600, color: annual ? MUTED : TEXT }}>Monthly</span>
            <button
              onClick={() => setAnnual(!annual)}
              aria-label="Toggle annual billing"
              style={{ position: "relative", width: 52, height: 28, borderRadius: 14, border: "none", background: annual ? BLUE : BORDER, cursor: "pointer", transition: "background 0.2s", padding: 0, flexShrink: 0 }}
            >
              <div style={{ position: "absolute", top: 3, left: annual ? 27 : 3, width: 22, height: 22, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
            </button>
            <span style={{ fontSize: 15, fontWeight: annual ? 600 : 400, color: annual ? TEXT : MUTED }}>
              Annual
              <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 600, color: "#fff", background: GREEN, borderRadius: 10, padding: "3px 10px", verticalAlign: "middle" }}>Save 20%</span>
            </span>
          </div>

          <div className="pricing-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0, border: `1px solid ${BORDER}`, borderRadius: 12, overflow: "hidden" }}>
            {PLANS.map((plan, i) => {
              const price = annual ? plan.yearly : plan.monthly;
              const monthlyEquiv = annual ? plan.yearly : null;
              const annualTotal = annual ? Math.round(plan.yearly * 12 * 100) / 100 : null;
              const monthlySaved = annual ? plan.monthly - plan.yearly : 0;
              return (
                <div key={plan.name} className="fade-up" style={{ background: plan.featured ? BLUE : CARD, color: plan.featured ? "#fff" : TEXT, padding: "40px 32px", borderRight: i < PLANS.length - 1 ? `1px solid ${plan.featured ? "rgba(255,255,255,0.15)" : BORDER}` : "none", position: "relative" }}>
                  {plan.badge && (
                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, background: BLUE_DK, textAlign: "center", fontSize: 11, fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase", padding: "6px", color: "#fff" }}>
                      {plan.badge}
                    </div>
                  )}
                  <div style={{ marginTop: plan.badge ? 20 : 0 }}>
                    <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 26, fontWeight: 600, marginBottom: 8 }}>{plan.name}</div>
                    <div style={{ fontSize: 44, fontWeight: 300, letterSpacing: "-1px", marginBottom: 4 }}>
                      ${Number.isInteger(price) ? price : price.toFixed(2)}<span style={{ fontSize: 15, color: plan.featured ? "rgba(255,255,255,0.55)" : MUTED }}>/mo</span>
                    </div>
                    {annual ? (
                      <div style={{ fontSize: 13, color: plan.featured ? "rgba(255,255,255,0.6)" : MUTED, marginBottom: 24 }}>
                        Billed ${annualTotal.toFixed(2)}/yr · Save ${(monthlySaved * 12).toFixed(0)}/yr
                      </div>
                    ) : (
                      <div style={{ fontSize: 14, color: plan.featured ? "rgba(255,255,255,0.7)" : MUTED, marginBottom: 24, fontStyle: "italic" }}>{plan.tagline}</div>
                    )}
                    <div style={{ height: 1, background: plan.featured ? "rgba(255,255,255,0.18)" : BORDER, marginBottom: 24 }} />
                    <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 32 }}>
                      {plan.features.map(f => (
                        <div key={f} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14 }}>
                          <span style={{ color: plan.featured ? "#90EEB0" : GREEN, fontSize: 15, flexShrink: 0 }}>✓</span>
                          <span style={{ color: plan.featured ? "rgba(255,255,255,0.85)" : TEXT }}>{f}</span>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => onStart && onStart(plan.slug)}
                      style={{ width: "100%", padding: "14px", border: plan.featured ? "none" : `1px solid ${BORDER}`, borderRadius: 24, background: plan.featured ? "#fff" : "transparent", color: plan.featured ? BLUE : BLUE, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", transition: "all 0.15s", minHeight: 44 }}
                    >
                      Start Free Trial →
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <p style={{ textAlign: "center", marginTop: 20, fontSize: 14, color: MUTED }}>All plans include a 30-day free trial · No credit card required · Cancel anytime</p>
        </div>
      </section>

      {/* ── TESTIMONIALS ─────────────────────────────────────────────── */}
      <section id="reviews" className="page-section" style={{ padding: "96px 48px", borderBottom: `1px solid ${BORDER}`, background: CARD }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase", color: BLUE, marginBottom: 12 }}>What people say</div>
          <h2 className="section-h2 fade-up" style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 42, fontWeight: 700, lineHeight: 1.15, marginBottom: 56 }}>
            Real results from<br /><em style={{ color: BLUE }}>real professionals</em>
          </h2>
          <div className="testi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, background: BORDER, border: `1px solid ${BORDER}`, borderRadius: 12, overflow: "hidden" }}>
            {TESTIMONIALS.map(t => (
              <div key={t.name} className="fade-up" style={{ background: CARD, padding: "36px 32px" }}>
                <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 19, fontStyle: "italic", lineHeight: 1.55, color: TEXT, marginBottom: 28 }}>"{t.quote}"</div>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ width: 44, height: 44, borderRadius: "50%", background: BLUE_LT, border: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Playfair Display',Georgia,serif", fontSize: 18, color: BLUE, fontWeight: 700, flexShrink: 0 }}>{t.init}</div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>{t.name}</div>
                    <div style={{ fontSize: 13, color: MUTED }}>{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────── */}
      <section className="page-section" style={{ padding: "96px 48px", background: TEXT, textAlign: "center" }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase", color: "#4FC3F7", marginBottom: 16 }}>Get started today</div>
          <h2 style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 50, fontWeight: 700, lineHeight: 1.15, color: "#fff", marginBottom: 16 }}>
            Maintain authority<br /><em style={{ color: "#4FC3F7" }}>while operating your business.</em>
          </h2>
          <p style={{ fontSize: 18, color: "rgba(255,255,255,0.55)", lineHeight: 1.7, marginBottom: 36 }}>
            Join 500+ professionals who systemized their LinkedIn presence and got back to what matters.
          </p>
          <button className="btn-primary" onClick={onStart} style={{ padding: "18px 48px", fontSize: 17, background: BLUE }}>
            Start Free — 30 Days →
          </button>
          <div style={{ marginTop: 16, fontSize: 13, color: "rgba(255,255,255,0.35)" }}>No credit card required during trial · Cancel anytime</div>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────────── */}
      <footer style={{ background: CARD, borderTop: `1px solid ${BORDER}`, padding: "32px 48px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <BrandLogo size="footer" showSub={true} href="https://brandhives.com" />
        <div style={{ display: "flex", gap: 28 }}>
          <a href="#/privacy-policy" style={{ fontSize: 14, color: MUTED, textDecoration: "none" }}>Privacy</a>
          <a href="#/terms" style={{ fontSize: 14, color: MUTED, textDecoration: "none" }}>Terms</a>
          <a href="mailto:operator@bhive.ca" style={{ fontSize: 14, color: MUTED, textDecoration: "none" }}>Contact</a>
        </div>
      </footer>
      <div style={{ background: BG, borderTop: `1px solid ${BORDER}`, padding: "14px 24px", textAlign: "center" }}>
        <span style={{ fontSize: 12, color: "#999" }}>AI by Claude · Payments secured by Stripe · Developed by BHIVE™ Labs</span>
      </div>
    </div>
  );
}
